// Single source of truth for what Swiftix VIP actually unlocks.
// Every row here must match a real gate in the code. If a gate changes, change it here.

import type { LucideIcon } from 'lucide-react';
import {
  Archive, Gauge, SlidersHorizontal, Palette, Compass, Puzzle, RotateCcw, Bookmark, Quote, Tv,
  Microscope, PieChart, TrendingUp, CalendarRange, Users, Dices, FileDown, Play, FolderPlus, Armchair, Crown,
} from 'lucide-react';

export interface VipPerk {
  id: string;
  /** Where in the theatre this perk lives */
  area: 'discovery' | 'insights' | 'planning' | 'tools';
  title: string;
  blurb: string;
  /** What a free member gets */
  free: string;
  /** What a VIP gets */
  vip: string;
  emoji: string;
  /** Icon that depicts this perk */
  icon: LucideIcon;
  /** Route a VIP can open to use it */
  route?: string;
  /** VIP-only (free members get nothing at all) */
  exclusive: boolean;
  /** Headline perks shown in compact lists */
  headline?: boolean;
}

export const VIP_PERKS: VipPerk[] = [
  {
    id: 'vault',
    area: 'discovery',
    title: 'Cinema Vault',
    blurb: 'The curated hidden-gem vault — genre rooms, obscurity dial, mood collections.',
    free: 'Locked',
    vip: 'Full access',
    emoji: '💎',
    icon: Archive,
    route: '/?tab=vault',
    exclusive: true,
    headline: true,
  },
  {
    id: 'searches',
    area: 'discovery',
    title: 'Monthly searches',
    blurb: 'One shared allowance for Deep Mood Search and Quick Discovery. Mood search itself is open to everyone.',
    free: '15 / month',
    vip: '100 / month',
    emoji: '🚀',
    icon: Gauge,
    route: '/?tab=discover',
    exclusive: false,
    headline: true,
  },
  {
    id: 'quick-discovery',
    area: 'discovery',
    title: 'Deeper Quick Discovery results',
    blurb: 'Each Quick Discovery search returns a longer list of titles.',
    free: '12 results per search',
    vip: '30 results per search',
    emoji: '🎯',
    icon: SlidersHorizontal,
    route: '/?tab=discover',
    exclusive: false,
    headline: true,
  },
  {
    id: 'mood-stacking',
    area: 'discovery',
    title: 'Mood stacking',
    blurb: 'Blend several moods into one request instead of searching one at a time.',
    free: '1 mood',
    vip: 'Up to 5 moods',
    emoji: '🎭',
    icon: Palette,
    route: '/?tab=discover',
    exclusive: false,
  },
  {
    id: 'ai-reasons',
    area: 'discovery',
    title: 'Why this pick',
    blurb: 'Per-title reasoning explaining why a result matches your mood.',
    free: '—',
    vip: 'On every result',
    emoji: '🧠',
    icon: Quote,
    route: '/?tab=discover',
    exclusive: true,
  },
  {
    id: 'vault-depth',
    area: 'discovery',
    title: 'Obscurity slider',
    blurb: 'In Quick Discovery, slide past mainstream picks into deep cuts and hidden gems.',
    free: '3 previews / month',
    vip: 'Always open',
    emoji: '🕳️',
    icon: Compass,
    route: '/?tab=discover',
    exclusive: false,
    headline: true,
  },
  {
    id: 'genre-stacking',
    area: 'discovery',
    title: 'Genre stacking',
    blurb: 'Combine as many genres as you like in one request.',
    free: '2 genres · 3 previews / month',
    vip: 'Unlimited',
    emoji: '🧩',
    icon: Puzzle,
    route: '/?tab=discover',
    exclusive: false,
  },
  {
    id: 'rerolls',
    area: 'discovery',
    title: 'Free re-rolls',
    blurb: 'Reshuffle the same request as often as you like without spending a search.',
    free: '—',
    vip: 'Unlimited',
    emoji: '🔄',
    icon: RotateCcw,
    route: '/?tab=discover',
    exclusive: true,
    headline: true,
  },
  {
    id: 'presets',
    area: 'discovery',
    title: 'Saved setups',
    blurb: 'Keep favourite mood-and-dial combinations on a shelf, one tap away.',
    free: '—',
    vip: 'Up to 6',
    emoji: '🔖',
    icon: Bookmark,
    route: '/?tab=discover',
    exclusive: true,
  },
  {
    id: 'tv',
    area: 'discovery',
    title: 'TV shows & series',
    blurb: 'Series, anime and limited runs across search and discovery.',
    free: '—',
    vip: 'Included',
    emoji: '📺',
    icon: Tv,
    route: '/?tab=discover',
    exclusive: true,
    headline: true,
  },
  {
    id: 'analysis',
    area: 'insights',
    title: 'Movie Analysis',
    blurb: 'Critic-grade breakdowns: themes, tone, craft, and why it works.',
    free: '—',
    vip: 'Unlimited',
    emoji: '🔬',
    icon: Microscope,
    route: '/?tab=analyze',
    exclusive: true,
    headline: true,
  },
  {
    id: 'taste-profile',
    area: 'insights',
    title: 'Taste Profile',
    blurb: 'Your most-watched directors, actors, eras and languages, calculated from your watch history.',
    free: '—',
    vip: 'Included',
    emoji: '📊',
    icon: PieChart,
    route: '/profile?tab=year',
    exclusive: true,
    headline: true,
  },
  {
    id: 'deep-stats',
    area: 'insights',
    title: 'Year in Review deep stats',
    blurb: 'Watch streaks, rating distribution and genre breakdowns.',
    free: 'Basic counts',
    vip: 'Full breakdown',
    emoji: '📈',
    icon: TrendingUp,
    route: '/profile?tab=year',
    exclusive: false,
  },
  {
    id: 'planner',
    area: 'planning',
    title: 'Watch Planner',
    blurb: 'Marathon schedules built around your free evenings.',
    free: '1 active schedule · 5 titles',
    vip: 'Unlimited · prerequisites · reminders',
    emoji: '🗓️',
    icon: CalendarRange,
    route: '/watch-planner',
    exclusive: false,
  },
  {
    id: 'movie-night',
    area: 'planning',
    title: 'Movie Night',
    blurb: 'Host a night, invite friends by handle, vote and email the winner.',
    free: '—',
    vip: 'Full access',
    emoji: '🎬',
    icon: Users,
    route: '/movie-night',
    exclusive: true,
  },
  {
    id: 'random',
    area: 'discovery',
    title: 'Random picks',
    blurb: 'Let the theatre choose for you.',
    free: '10 / day',
    vip: 'Unlimited',
    emoji: '🎲',
    icon: Dices,
    route: '/?tab=random',
    exclusive: false,
  },
  {
    id: 'export',
    area: 'tools',
    title: 'Watchlist export',
    blurb: 'Take your library out as PDF, CSV, or a Letterboxd-ready file.',
    free: '—',
    vip: 'Included',
    emoji: '📥',
    icon: FileDown,
    route: '/profile?tab=activity',
    exclusive: true,
  },
  {
    id: 'trailers',
    area: 'tools',
    title: 'In-app trailers',
    blurb: 'Trailers play inside the theatre instead of bouncing to YouTube.',
    free: 'Opens YouTube',
    vip: 'Plays in app',
    emoji: '▶️',
    icon: Play,
    exclusive: false,
  },
  {
    id: 'collection-quick-add',
    area: 'tools',
    title: 'One-tap collection add',
    blurb: 'Drop any title straight into a collection from its card. Collections themselves are free for everyone.',
    free: 'Add from the collection page',
    vip: 'Add from any card',
    emoji: '📚',
    icon: FolderPlus,
    route: '/collections',
    exclusive: false,
  },
  {
    id: 'vip-lounge',
    area: 'insights',
    title: 'VIP Lounge',
    blurb: 'Your own dashboard: allowance left, shortcuts to every perk, and your activity at a glance.',
    free: '—',
    vip: 'Included',
    emoji: '🛋️',
    icon: Armchair,
    route: '/vip',
    exclusive: true,
  },
  {
    id: 'vip-badge',
    area: 'tools',
    title: 'VIP badge',
    blurb: 'A VIP badge on your public profile and next to your name around the theatre.',
    free: '—',
    vip: 'Included',
    emoji: '👑',
    icon: Crown,
    route: '/profile',
    exclusive: true,
  },
];

export const VIP_HEADLINE_PERKS = VIP_PERKS.filter((p) => p.headline);

export const VIP_COMPARISON_ROWS = VIP_PERKS.map((p) => ({
  label: p.title,
  free: p.free,
  vip: p.vip,
}));
